import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Shared UI state primitives for the public site + vendor dashboard.
 * (The admin console keeps its own set in src/pages/admin/ui.tsx.)
 *
 * Goal: one consistent treatment for loading / empty / error across every
 * data-driven page, instead of bare <Loader2> spinners and "No items" text.
 */

/* ---- Confirm dialog ------------------------------------------------------ */

/** Centered confirmation modal. Renders nothing when `open` is false. */
export function ConfirmDialog({
  open, title, message, confirmLabel, cancelLabel, onConfirm, onCancel, danger = false,
}: {
  open: boolean;
  title: string;
  message?: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  danger?: boolean;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <h2 className="font-display text-lg text-stone-950">{title}</h2>
        {message && <p className="text-sm text-stone-500 mt-2">{message}</p>}
        <div className="flex gap-2 mt-6">
          <button onClick={onCancel} className="btn-secondary flex-1 text-sm">{cancelLabel}</button>
          <button onClick={onConfirm}
            className={`flex-1 text-sm rounded-lg px-4 py-2 font-medium text-white transition-colors ${
              danger ? 'bg-red-600 hover:bg-red-700' : 'bg-stone-950 hover:bg-stone-800'
            }`}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---- Skeleton ----------------------------------------------------------- */

/** A single shimmering placeholder block. Size it with `className`. */
export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`skeleton ${className}`} aria-hidden="true" />;
}

/** A stack of skeleton lines (last line shortened) for text placeholders. */
export function SkeletonText({ lines = 3, className = '' }: { lines?: number; className?: string }) {
  return (
    <div className={`space-y-2 ${className}`} aria-hidden="true">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className={`h-3.5 ${i === lines - 1 ? 'w-2/3' : 'w-full'}`} />
      ))}
    </div>
  );
}

/** A card-shaped placeholder approximating a listing/inventory row. */
export function SkeletonCard({ className = '' }: { className?: string }) {
  return (
    <div className={`card overflow-hidden ${className}`} aria-hidden="true">
      <Skeleton className="h-36 w-full rounded-none" />
      <div className="space-y-2.5 p-4">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
        <div className="flex gap-2 pt-1">
          <Skeleton className="h-5 w-14 rounded-full" />
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
      </div>
    </div>
  );
}

/** A grid of skeleton cards — drop-in for a listing/inventory grid while loading. */
export function SkeletonGrid({ count = 6, className = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4' }: {
  count?: number; className?: string;
}) {
  return (
    <div className={className}>
      {Array.from({ length: count }).map((_, i) => <SkeletonCard key={i} />)}
    </div>
  );
}

/* ---- Spinner ------------------------------------------------------------ */

/** Centered spinner. Use `full` to fill the parent height. */
export function Spinner({ full = false, label, size = 24 }: { full?: boolean; label?: string; size?: number }) {
  return (
    <div className={`flex flex-col items-center justify-center gap-3 ${full ? 'h-full min-h-48' : 'py-10'}`} role="status">
      <Loader2 size={size} className="animate-spin text-stone-400" />
      {label && <span className="text-xs text-stone-400">{label}</span>}
      <span className="sr-only">{label || 'Loading'}</span>
    </div>
  );
}

/* ---- Empty state -------------------------------------------------------- */

export function EmptyState({ icon: Icon, title, description, action, className = '' }: {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex flex-col items-center justify-center px-6 py-12 text-center ${className}`}>
      {Icon && (
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-stone-100 text-stone-400">
          <Icon size={22} />
        </div>
      )}
      <h3 className="text-sm font-semibold text-stone-900">{title}</h3>
      {description && <p className="mt-1 max-w-xs text-sm text-stone-400">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/* ---- Error state -------------------------------------------------------- */

export function ErrorState({ title, message, onRetry, retryLabel = 'Retry', className = '' }: {
  title?: string;
  message?: string;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
}) {
  return (
    <div className={`flex flex-col items-center justify-center px-6 py-12 text-center ${className}`} role="alert">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-danger-soft text-danger">
        <AlertCircle size={22} />
      </div>
      <h3 className="text-sm font-semibold text-stone-900">{title || 'Something went wrong'}</h3>
      {message && <p className="mt-1 max-w-xs text-sm text-stone-400">{message}</p>}
      {onRetry && (
        <button onClick={onRetry} className="btn-secondary mt-5 gap-2">
          <RefreshCw size={14} />
          {retryLabel}
        </button>
      )}
    </div>
  );
}

/* ---- Inline alert (compact, for forms / banners) ------------------------ */

type AlertTone = 'success' | 'warning' | 'danger' | 'info';
const ALERT_TONE: Record<AlertTone, string> = {
  success: 'bg-success-soft text-success-dark',
  warning: 'bg-warning-soft text-warning-dark',
  danger: 'bg-danger-soft text-danger-dark',
  info: 'bg-info-soft text-info-dark',
};

export function InlineAlert({ tone = 'info', icon: Icon, children, className = '' }: {
  tone?: AlertTone;
  icon?: LucideIcon;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex items-start gap-2 rounded-lg px-3 py-2.5 text-sm ${ALERT_TONE[tone]} ${className}`} role="status">
      {Icon && <Icon size={16} className="mt-0.5 shrink-0" />}
      <div className="min-w-0">{children}</div>
    </div>
  );
}
